import { useEffect, useState } from 'react';
import { api, readErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/toast';

const STATUS_COLORS = {
    pending_payment: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    paid: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    scheduled: 'bg-marigold/15 text-marigold border-marigold/30',
    completed: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
    cancelled: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
};

const NEXT_STATUSES = ['pending_payment', 'paid', 'scheduled', 'completed', 'cancelled'];

export default function AdminConsultations() {
    const [data, setData] = useState({ rows: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (statusFilter !== 'all') params.status = statusFilter;
            const { data } = await api.get('/admin/consultations', { params });
            setData(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const updateStatus = async (id, status) => {
        try {
            const { data: updated } = await api.patch(`/admin/consultations/${id}`, { status });
            setData(d => ({ ...d, rows: d.rows.map(r => r.id === id ? { ...r, ...updated } : r) }));
            toast.success(`Status: ${status}`);
        } catch (e) {
            toast.error(readErr(e, 'Failed'));
        }
    };

    return (
        <div className="p-6 lg:p-10 space-y-6" data-testid="admin-consultations">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Consultations</div>
                    <h1 className="font-serif text-3xl text-parchment mt-2">{data.total} bookings</h1>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-44 bg-elevated border-marigold/15 text-parchment rounded-sm" data-testid="consult-status-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-marigold/15 text-parchment">
                        <SelectItem value="all">All statuses</SelectItem>
                        {NEXT_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <Card className="rounded-sm bg-elevated border-marigold/10 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-marigold/10">
                            <tr>
                                <th className="px-4 py-3">Booked</th>
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">Expert</th>
                                <th className="px-4 py-3">Tier</th>
                                <th className="px-4 py-3">Amount</th>
                                <th className="px-4 py-3">Scheduled</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="8" className="px-4 py-6 text-slate-500">Loading…</td></tr>}
                            {!loading && data.rows.length === 0 && (
                                <tr><td colSpan="8" className="px-4 py-6 text-slate-500" data-testid="consults-empty">No consultations.</td></tr>
                            )}
                            {data.rows.map(r => (
                                <tr key={r.id} className="border-b border-marigold/5 hover:bg-obsidian/40" data-testid={`consult-row-${r.id}`}>
                                    <td className="px-4 py-3 text-slate-500 text-xs">{r.created_at?.slice(0, 10)}</td>
                                    <td className="px-4 py-3 text-slate-300">{r.user_email || r.user_id}</td>
                                    <td className="px-4 py-3 text-parchment">{r.expert_name}</td>
                                    <td className="px-4 py-3 text-slate-400">{r.duration_min}m</td>
                                    <td className="px-4 py-3 text-parchment">₹{Number(r.amount_inr || 0).toLocaleString('en-IN')}</td>
                                    <td className="px-4 py-3 text-slate-500 text-xs">{r.scheduled_at ? r.scheduled_at.slice(0, 16).replace('T', ' ') : '—'}</td>
                                    <td className="px-4 py-3">
                                        <Badge className={`${STATUS_COLORS[r.status] || STATUS_COLORS.pending_payment} hover:bg-transparent`}>{r.status?.replace('_', ' ')}</Badge>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-1">
                                            {r.status !== 'completed' && (
                                                <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, 'completed')} className="bg-transparent border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 rounded-sm h-7 px-2 text-xs" data-testid={`mark-completed-${r.id}`}>
                                                    Complete
                                                </Button>
                                            )}
                                            {r.status !== 'cancelled' && (
                                                <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, 'cancelled')} className="bg-transparent border-rose-500/30 text-rose-400 hover:bg-rose-500/10 rounded-sm h-7 px-2 text-xs" data-testid={`mark-cancelled-${r.id}`}>
                                                    Cancel
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
